/**
 * Translates a `ChartMarkEvent`'s `field`/`seriesField` — read off the
 * RESOLVED config so shaping/rendering can read rows by KEY (P2, the
 * duplicate-column-dedup fix) — back to the identity the dashboards binding
 * layer matches on: the column's display NAME (`ColumnTypeInfo.name`, which
 * is `Column N` for a blank-named column — so not always a valid SQL
 * identifier; `buildFilterPredicate` rejects such a field, as it rejected
 * `''` before), never the internal row key — UNLESS the name is ambiguous
 * (shared by more than one result column, e.g. an MSSQL join returning both
 * `orders.region` and `customers.region` unaliased, both named `region`),
 * in which case a name would silently bind to whichever of the two columns
 * a persisted binding happens to match first.
 *
 * Confirmed by reading the consumers: `markEventToFilters`/`fieldFilter`
 * (crossFilter.ts) matches `event.field` against `FilterBinding.targetExpression`
 * / `.dashboardField` case-insensitively, both derived from the VQE query
 * structure (the output alias / the resolved underlying column, QUALIFIED
 * `alias.column` when more than one relation is in scope), never from the
 * result's `ColumnTypeInfo` (`deriveVqeFilterBindings`,
 * vqeBindingDerivation.ts); with no matching
 * binding it falls back to using `event.field` itself as `dashboardField`,
 * which `drillSqlForTile` (tileNavigation.ts) then inlines as
 * `binding?.targetExpression ?? f.dashboardField` directly into generated
 * SQL — it must already be a valid, real identifier, never `col_<i>`.
 *
 * Rule per axis (X via `field`, series via `seriesField`):
 *   1. If the PERSISTED (unresolved) config held something different from
 *      the resolved key, that difference IS the pre-rekey name (or an
 *      unresolved/ambiguous reference) — emit it verbatim. (Resolution only
 *      succeeds via a UNIQUE name match, so this name was already proven
 *      unambiguous at resolve time.)
 *   2. Otherwise the persisted config was already key-shaped (or key and
 *      name coincide, the common non-duplicate case) — look the resolved
 *      key up in the columns list:
 *      a. Its NAME is unambiguous (no other column shares it) — emit the
 *         name, as before.
 *      b. Its NAME is shared by another column (an unaliased MSSQL join
 *         returning both `orders.region` and `customers.region`) — a name
 *         here would let `fieldFilter` bind the click to whichever
 *         same-named column a persisted binding lists first, and the
 *         internal KEY must never reach the filter store either: with no
 *         binding `fieldFilter` keeps it as `dashboardField`, the chip
 *         would read `col_1 = EU` and `drillSqlForTile` would inline
 *         `WHERE col_1 = 'EU'` against a column that does not exist. So:
 *         emit the KEY when a persisted `FilterBinding` already names it;
 *         otherwise the identity is UNRESOLVABLE and the whole mark event
 *         is dropped (`null`) — no filter, no drill. (An STS duplicate key
 *         is `baseTable.column`, while `deriveVqeFilterBindings` qualifies
 *         with the relation ALIAS when one exists and targets the
 *         UNDERLYING column name — so the key matches a derived binding
 *         only for an unaliased join whose duplicated columns are not
 *         themselves aliased. Any other same-named pair drops both marks.
 *         Follow-up: match bindings via the column's
 *         `sourceTable`/`sourceColumn` provenance instead.)
 */
import type { ChartConfig, ChartMarkEvent } from '../types/chartTypes';

/** A result column as `{key, name}` — same shape `chartColumnRefs.ts` uses. */
export type MarkEventBindingColumn = { key: string; name: string };

/** Minimal shape of a persisted cross-filter binding (`FilterBinding`, dashboardTypes.ts). */
export type MarkEventFilterBinding = { targetExpression: string; dashboardField: string };

/** `undefined` = no reference on this axis; `null` = unresolvable (drop the event). */
function bindingIdentity(
  originalRef: string | undefined,
  resolvedRef: string | undefined,
  columns: readonly MarkEventBindingColumn[],
  filterBindings: readonly MarkEventFilterBinding[],
): string | null | undefined {
  if (resolvedRef === undefined) return undefined;
  if (originalRef !== undefined && originalRef !== resolvedRef) return originalRef;

  const name = columns.find((c) => c.key === resolvedRef)?.name;
  if (name === undefined) return resolvedRef;

  const ambiguous = columns.filter((c) => c.name === name).length > 1;
  if (!ambiguous) return name;

  const lowerKey = resolvedRef.toLowerCase();
  const keyIsBound = filterBindings.some(
    (b) => b.targetExpression.toLowerCase() === lowerKey || b.dashboardField.toLowerCase() === lowerKey,
  );
  return keyIsBound ? resolvedRef : null;
}

/**
 * Returns `event` with `field`/`seriesField` translated to the binding
 * identity, per the rule above, or `null` when an identity cannot be
 * established (an ambiguous name with no binding naming the key) — the
 * caller must then ignore the mark. `originalConfig` is the PERSISTED config
 * (e.g. `tile.chartConfig`, before `resolveChartConfigColumnRefs`);
 * `resolvedConfig` is what `event` was actually built from (shaping/mark
 * resolution ran against it); `filterBindings` is the tile's persisted
 * `FilterBinding[]`. Other event fields (`value`, `seriesValue`) pass through
 * untouched — they're already datum values, not column refs.
 */
export function bindingIdentityForMarkEvent(
  event: ChartMarkEvent,
  originalConfig: ChartConfig,
  resolvedConfig: ChartConfig,
  columns: readonly MarkEventBindingColumn[],
  filterBindings: readonly MarkEventFilterBinding[] = [],
): ChartMarkEvent | null {
  const field = bindingIdentity(originalConfig.xAxis?.column, resolvedConfig.xAxis?.column, columns, filterBindings);
  if (field === null) return null;

  if (event.seriesField === undefined) {
    return { ...event, field: field ?? event.field };
  }

  const seriesField = bindingIdentity(
    originalConfig.series?.column,
    resolvedConfig.series?.column,
    columns,
    filterBindings,
  );
  if (seriesField === null) return null;
  return {
    ...event,
    field: field ?? event.field,
    seriesField: seriesField ?? event.seriesField,
  };
}
